import React, { useEffect, useState } from "react";
import { Grid, TextField, Button, Box, Typography, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { IoClose } from "react-icons/io5";
import { IoIosCloseCircle } from "react-icons/io";
import useAuthStore from "store/authStore";
import axios from "axios";
import { BASE_URL } from "service";
import WithDrawHistory from "./withdrawHistory";

function WithdrawlHome() {
  const { email, phone } = useAuthStore();
  const [upiId, setUpiId] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  console.log("email phone , ", email, phone);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/earning/dailyselfearning/?email=${email}`
        );
        console.log("Earnings response:", response.data);
        setEarnings(response.data.earnings);
      } catch (error) {
        console.error("Error fetching earnings data:", error);
      }
    };

    if (email) fetchEarnings();
  }, [email]);

  const handleApply = async () => {
    if (!email || !upiId || !withdrawalAmount) {
      console.log("Validation failed: Missing fields");
      setSnackbarMessage("Please fill all fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    if (isNaN(amount) || amount <= 0) {
      console.log("Validation failed: Invalid amount");
      setSnackbarMessage("Please enter a valid amount.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (earnings?.available_balance < amount) {
      console.log("Validation failed: Insufficient balance");
      setInsufficientBalance(true);
      setSnackbarMessage("Insufficient available balance!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsProcessing(true);
    try {
      console.log("Sending withdrawal request:", { upiId, amount, email });
      const response = await axios.post(`${BASE_URL}/api/withdrawal/?email=${email}`, {
        upiId: upiId,
        amount: amount.toString(),
        paymentStatus: "pending",
      });

      console.log("Withdrawal response:", response);
      if (response.status === 201) {
        console.log("Withdrawal successful, setting success state");
        setIsSuccess(true);
        setUpiId("");
        setWithdrawalAmount("");
        setSnackbarMessage("Success! Withdrawal done, please wait for 24 hours.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        // Refresh earnings data
        const earningsResponse = await axios.get(
          `${BASE_URL}/api/earning/dailyselfearning/?email=${email}`
        );
        console.log("Updated earnings:", earningsResponse.data);
        setEarnings(earningsResponse.data.earnings);
      } else {
        console.log("Unexpected response status:", response.status);
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Withdrawal failed:", error);
      setSnackbarMessage("Withdrawal failed. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsProcessing(false);
      setInsufficientBalance(false);
    }
  };

  const handleCloseSuccessCard = () => setIsSuccess(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    console.log("Closing snackbar");
    setSnackbarOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid mt={3} item xs={12} md={6} lg={3}>
        <MDBox mb={1.5}>
          <ComplexStatisticsCard
            color="error"
            icon="weekend"
            iconType="PiHandWithdrawFill"
            title="Total Amount"
            count={earnings?.available_balance || "0"}
            percentage={{
              color: "success",
              amount: "Total",
              label: "Self Income + Direct Income + Team Income",
            }}
          />
        </MDBox>
      </Grid>

      <Grid item xs={12} md={6} lg={4}>
        {isProcessing && (
          <MDBox mt={4} mb={1.5}>
            <ComplexStatisticsCard
              color="warning"
              iconType="TbProgressAlert"
              title="Withdrawal in Progress"
              count={`₹ ${Number(withdrawalAmount).toFixed(2)}`}
              percentage={{
                color: "info",
                amount: "",
                label: "Admin will approve your payment within 24 - 32 hours",
              }}
            />
          </MDBox>
        )}

        {isSuccess && (
          <MDBox mt={3} mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              iconType="TbProgressCheck"
              title="Transferred Successfully"
              count={`₹ ${withdrawalAmount}`}
              percentage={{
                color: "error",
                amount: (
                  <div onClick={handleCloseSuccessCard}>
                    <IoIosCloseCircle size={30} />
                  </div>
                ),
                label: "Funds have been transferred successfully",
              }}
              action={
                <Box
                  onClick={handleCloseSuccessCard}
                  sx={{ cursor: "pointer", position: "absolute", top: 10, right: 10 }}
                >
                  <IoClose size={20} color="gray" />
                </Box>
              }
            />
          </MDBox>
        )}
      </Grid>

      <Grid container mt={3} spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <MDBox
            sx={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "rgba(0, 0, 0, 0.15) 0px 5px 15px",
            }}
            mb={1.5}
          >
            <Typography variant="h5">Withdraw Money</Typography>
            <form noValidate autoComplete="off">
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                margin="normal"
                value={email}
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="UPI ID"
                variant="outlined"
                margin="normal"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="₹ Withdrawal Amount"
                variant="outlined"
                margin="normal"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                required
              />
              {insufficientBalance && (
                <Typography color="error" variant="body2">
                  Insufficient available balance!
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: "linear-gradient(195deg, #66BB6A, #43A047)",
                  mt: 2,
                  color: "#fff !important",
                  "&:disabled": { backgroundColor: "#e0e0e0" },
                }}
                onClick={handleApply}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Apply for Withdrawal"}
              </Button>
            </form>
          </MDBox>
        </Grid>
      </Grid>

      <WithDrawHistory />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          zIndex: 1500, // Ensure Snackbar appears above other elements
          "& .MuiSnackbarContent-root": {
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: 500,
            backgroundColor: snackbarSeverity === "success" ? "#4CAF50" : "#F44336",
            color: "#fff",
            "& .MuiAlert-icon": {
              color: "#fff",
            },
          }}
          role="alert"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default WithdrawlHome;
